import React from 'react';

interface StatusIndicatorProps {
  status?: string;
  reasons?: string[];
  // Things worth knowing that are not faults, shown in grey under the verdict.
  notes?: string[];
}

/**
 * The Pi's notes: facts about the machine that are not faults.
 *
 * No camera is the normal state of a site that never had one, so it is grey
 * here and never amber. Raised as a warning it would sit on every such device
 * for ever and teach people to ignore the colour. A camera plugged in that gives
 * no picture IS a fault, and arrives as a health reason instead.
 *
 * "Detected", not "connected": the Pi knows only that it cannot find a camera.
 * The first device to show this had a camera fitted that it had not seen once
 * in eight days, and "No camera connected" read as simply wrong.
 */
export const healthNotes = (device: { cameraDetected?: boolean | null }): string[] =>
  device.cameraDetected === false ? ['No camera detected'] : [];

const pillClasses = (status?: string): string => {
  const s = (status || '').toLowerCase();
  if (s.startsWith('offline')) return 'bg-red-100 text-red-800';
  switch (s) {
    case 'online':
    case 'running':
      return 'bg-green-100 text-green-800';
    case 'not running':
      return 'bg-red-100 text-red-800';
    case 'maintenance':
      return 'bg-blue-100 text-blue-800';
    case 'pending':
    case 'unknown':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

// How long the device has been silent, in words. The raw values carry the
// bracket the API put it in, which is more use to somebody triaging a call than
// a bare "Offline".
const readableLabel = (status: string): string => {
  switch (status) {
    case 'Offline':
      return 'Offline (5-15 min)';
    case 'Offline+15':
      return 'Offline (15-30 min)';
    case 'Offline+30':
      return 'Offline (30+ min)';
    case 'Warning':
      // Only reached when there are no specific reasons to list. A bare
      // "Warning" tells nobody anything.
      return 'Issue';
    default:
      return status;
  }
};

/**
 * Shows one verdict about a device, with its specific reasons where there are
 * any.
 *
 * Used twice per row: once for the Pi's health and once for what Pinage is
 * doing. Those are two independent things - the machine can be perfectly well
 * while the signage software is stopped, and that combination used to be
 * invisible because a single column had to pick one story to tell.
 *
 * Both values arrive already computed by the API on every read, and that matters
 * more than it looks. The status this component used to render was a stored
 * column, written only when a report arrived, so it froze the moment a device
 * went quiet - which is how one came to show a green "Online" pill beside "last
 * seen 4 days ago". A device that is not talking to us cannot be described as
 * healthy whatever it last claimed, so the API now derives both verdicts from
 * how recently it actually heard from the device.
 *
 * Reasons are suppressed by the API while a device is silent, for the same
 * reason: they come from that final report and are just as frozen. "Monitor
 * disconnected" from four days ago sitting under an Offline pill sends somebody
 * after the wrong fault.
 */
/** The verdict alone, as a coloured pill. */
export const StatusPill: React.FC<{ status?: string }> = ({ status }) => (
  <span className={`px-2 py-1 text-xs rounded-full font-medium ${pillClasses(status)}`}>
    {readableLabel(status || 'Unknown')}
  </span>
);

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ status, reasons, notes }) => {
  const verdict = reasons && reasons.length > 0 ? (
    <ul className="text-xs text-amber-700 font-medium space-y-0.5">
      {reasons.map((reason, i) => (
        <li key={i} className="flex items-start">
          <span className="mr-1 leading-none">•</span>
          <span>{reason}</span>
        </li>
      ))}
    </ul>
  ) : (
    <StatusPill status={status} />
  );

  if (!notes || notes.length === 0) return verdict;

  return (
    <div>
      {verdict}
      <ul className="mt-2 text-xs text-gray-500 space-y-0.5">
        {notes.map((note, i) => (
          <li key={i}>{note}</li>
        ))}
      </ul>
    </div>
  );
};

export default StatusIndicator;
